/**
 * Site dashboard layout shell — context sidebar + main stage, responsive.
 *
 * The frame a single site's dashboard renders into: a collapsible
 * context sidebar (site identity, device, alarms, maintenance) that
 * scrolls independently beside the operational main stage (relay +
 * camera controls). On smaller screens the sidebar detaches into a
 * slide-out drawer opened from a sticky summary bar, keeping the
 * relay/camera controls front and centre. The alarm and maintenance
 * cards are the exception: on a phone looking at the site's own
 * dashboard they leave the drawer for the page flow, where they need no
 * discovery. `railExtrasPlacement` owns that decision and returns one
 * surface, so those cards never render twice.
 *
 * Collapsing the sidebar removes it from layout entirely — the stage
 * grows to full width with no residual edge affordance. The show/hide
 * control itself lives in the caller's TitleBar (see SiteDashboardLayout);
 * this component only reads the `collapsed` flag to decide whether to
 * render the rail at all.
 *
 * The drawer's own cards navigate (alarm history, maintenance history, the
 * command log), and the layout that owns this shell survives those routes, so
 * the sheet is anchored to the path it was opened over and closes the moment
 * the location leaves it (see resolveDetailsDrawer).
 *
 * Card content is injected by the caller; this file owns the layout and
 * opting the sidebar's cards into their flat "rail" treatment. It never
 * imports the cards themselves.
 *
 * Contains:
 * - SiteDashboardShell — the layout component (default export)
 */
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import DashboardCardVariantContext, {
  DASHBOARD_CARD_VARIANT,
} from '@sophocles/components/BasicDashboardCard/dashboardCardVariant';
import {
  DETAILS_DRAWER_CLOSED,
  openDetailsDrawer,
  resolveDetailsDrawer,
} from '@sophocles/views/sites/components/SiteDashboard/detailsDrawerState';

import { RAIL_EXTRAS_PLACEMENT, resolveRailExtrasPlacement } from './railExtrasPlacement';

const RAIL_WIDTH = 340;
const DRAWER_WIDTH = 'min(86vw, 380px)';
// Caps the sticky sidebar below the global app bar without forcing short rails
// to stretch taller than their content.
const RAIL_MAX_HEIGHT = 'calc(100vh - 88px)';
// Bounds the stage itself for subpages whose grid manages its own internal
// scrolling (e.g. proof-of-performance, status history) instead of relying on
// the dashboard's page-scroll. 88px mirrors RAIL_MAX_HEIGHT's app-bar offset;
// the extra 56px accounts for the back-button + TitleBar row the layout
// renders above the shell on those subpages.
const STAGE_MAX_HEIGHT = 'calc(100vh - 144px)';
// Compact counterpart: below lg the summary bar and stage share one
// viewport-tall flex column, and the stage takes the remaining space, so the
// summary bar's own (content-dependent) height needs no term. Only the fixed
// chrome above that column is subtracted — 16px for the app nav's bottom
// padding plus the same 56px back-button/TitleBar row the layout renders above
// the shell on md+ subpages (present below md as a harmless gap, absent).
const COMPACT_STAGE_MAX_HEIGHT = 'calc(100vh - 72px)';

// One white surface (matching the operational cards) so the stacked sections
// read as a single unified sidebar; dividers come from each rail card.
const sidebarSurfaceSx = {
  border: 0,
  borderRadius: 'var(--card-radius)',
  bgcolor: 'var(--site-rail-bg)',
  boxShadow: 'var(--site-rail-shadow)',
  overflow: 'hidden',
};

function SiteDashboardShell({
  rail,
  railExtras,
  inlineExtrasOnPhone = false,
  summary,
  children,
  evenlySpaceRail = false,
  collapsible = false,
  collapsed = false,
  stageScroll = 'page',
}) {
  const theme = useTheme();
  // Below lg the global app nav + a 340px sidebar leave no room for the
  // operational cards, so the sidebar detaches into a drawer and the
  // relay/camera stage takes the full width.
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const isPhone = useMediaQuery(theme.breakpoints.down('mobile'));
  const { pathname } = useLocation();

  const [drawerState, setDrawerState] = useState(DETAILS_DRAWER_CLOSED);
  // Resolved during render, not in an effect: the sheet has to be gone in the
  // same commit that paints the route it navigated to, with no frame in
  // between. Writing the reset back keeps the stored state honest, so
  // navigating back to the path the sheet was opened over does not reopen it.
  const resolvedDrawer = resolveDetailsDrawer(drawerState, pathname);
  if (resolvedDrawer !== drawerState) setDrawerState(resolvedDrawer);

  const isDrawerOpen = resolvedDrawer.isOpen;
  const openDetails = () => setDrawerState(openDetailsDrawer(pathname));
  const closeDetails = () => setDrawerState(DETAILS_DRAWER_CLOSED);

  const extrasPlacement = resolveRailExtrasPlacement({
    isCompact,
    isPhone,
    inlineOnPhone: inlineExtrasOnPhone,
    stageScroll,
  });

  const sidebarCards = (
    <DashboardCardVariantContext.Provider value={DASHBOARD_CARD_VARIANT.RAIL}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: evenlySpaceRail ? 'space-between' : 'flex-start',
        }}
      >
        {rail}
      </Box>
    </DashboardCardVariantContext.Provider>
  );

  if (isCompact) {
    const summaryContent =
      isPhone && React.isValidElement(summary)
        ? React.cloneElement(summary, { phone: true, onOpenDetails: openDetails })
        : summary;

    const summaryBar = (
      <Box
        onClick={isPhone ? undefined : openDetails}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          cursor: isPhone ? 'default' : 'pointer',
          bgcolor: isPhone ? 'var(--site-mobile-bar-bg)' : 'var(--surface-page)',
          borderBottom: isPhone ? 0 : '1px solid var(--border-default)',
          boxShadow: isPhone ? 'var(--site-mobile-bar-shadow)' : 'none',
        }}
      >
        {summaryContent}
      </Box>
    );

    const detailsDrawer = (
      <Drawer
        anchor={isPhone ? 'bottom' : 'left'}
        open={isDrawerOpen}
        onClose={closeDetails}
        PaperProps={{
          sx: {
            width: isPhone ? '100%' : DRAWER_WIDTH,
            maxHeight: isPhone ? '88dvh' : '100%',
            bgcolor: 'var(--card-bg)',
            overflowY: 'auto',
            ...(isPhone && {
              borderTopLeftRadius: 'var(--site-details-drawer-radius)',
              borderTopRightRadius: 'var(--site-details-drawer-radius)',
            }),
          },
        }}
      >
        {isPhone ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 'var(--space-2)', pb: 'var(--space-1)' }}>
            <Box
              sx={{
                width: 'var(--space-8)',
                height: 'var(--space-1)',
                borderRadius: 'var(--radius-xl)',
                bgcolor: 'var(--border-default)',
              }}
            />
          </Box>
        ) : null}
        {sidebarCards}
        {railExtras && extrasPlacement === RAIL_EXTRAS_PLACEMENT.DRAWER ? (
          <Box
            sx={{
              p: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
              bgcolor: 'var(--surface-page)',
              borderTop: '1px solid var(--border-default)',
              flex: 1,
            }}
          >
            {railExtras}
          </Box>
        ) : null}
      </Drawer>
    );

    // Inline placement puts the cards in the page flow under the stage, where a
    // phone reader scrolls onto them without opening anything.
    const inlineExtras =
      railExtras && extrasPlacement === RAIL_EXTRAS_PLACEMENT.STAGE ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', pb: 'var(--space-4)' }}>
          {railExtras}
        </Box>
      ) : null;

    // Contained subpages bound their own grid rather than page-scroll: the
    // summary bar and stage share a viewport-tall flex column so the grid's
    // virtualScroller — not the page — overflows, which is what arms MUI's
    // viewport-mode lazy loader. The stage takes the remaining height under the
    // summary bar; the ancestor content box is block-flow, so this explicit
    // bound (not flex alone) is what keeps the page from scrolling.
    if (stageScroll === 'contained') {
      return (
        <>
          <Box sx={{ height: COMPACT_STAGE_MAX_HEIGHT, display: 'flex', flexDirection: 'column' }}>
            {summaryBar}
            <Box
              sx={{
                px: 'var(--page-px)',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {children}
            </Box>
          </Box>
          {detailsDrawer}
        </>
      );
    }

    return (
      <>
        {summaryBar}
        <Box sx={{ px: 'var(--page-px)' }}>
          {children}
          {inlineExtras}
        </Box>
        {detailsDrawer}
      </>
    );
  }

  // Collapsed costs zero stage width: no rail, no edge affordance. The
  // show/hide control lives in the caller's TitleBar (see SiteDashboardLayout).
  const railIsOpen = !collapsible || !collapsed;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: railIsOpen ? 2 : 0, px: 'var(--page-px)' }}>
      {railIsOpen && (
        <Box
          sx={{
            flexShrink: 0,
            width: RAIL_WIDTH,
            position: 'sticky',
            top: 'var(--space-2)',
            alignSelf: 'flex-start',
            maxHeight: RAIL_MAX_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* One scroll region for the sidebar surface and any cards stacked
              beneath it, so the whole sticky column stays reachable on short
              viewports. */}
          <Box
            sx={{
              maxHeight: RAIL_MAX_HEIGHT,
              width: 'calc(100% + var(--site-rail-shadow-gutter) + var(--site-rail-shadow-gutter))',
              ml: 'calc(0px - var(--site-rail-shadow-gutter))',
              mt: 'calc(0px - var(--site-rail-shadow-gutter))',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              p: 'var(--site-rail-shadow-gutter)',
              // Match the stage's inter-card gap (DashboardSection uses gap: 2).
              gap: 'var(--space-4)',
              // On short viewports the column must scroll, not squeeze its
              // cards — flex children default to shrink, which crushed the
              // alarm/maintenance lists down to a single visible row.
              '& > *': { flexShrink: 0 },
            }}
          >
            <Box sx={sidebarSurfaceSx}>{sidebarCards}</Box>
            {railExtras}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ...(stageScroll === 'contained'
            ? { height: STAGE_MAX_HEIGHT, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
            : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

SiteDashboardShell.propTypes = {
  /** When true, distribute rail sections across the available full-height sidebar. */
  evenlySpaceRail: PropTypes.bool,
  /** Context cards (site / device / commands / media) — sidebar body and drawer body. */
  rail: PropTypes.node,
  /** Standalone cards stacked under the sidebar surface (desktop) — e.g. the
   * alarm and maintenance summary cards. On compact screens they render inline
   * in the stage below the operational cards on page-scroll subroutes, and stay
   * in the details drawer on contained ones (whose bounded stage would clip them). */
  railExtras: PropTypes.node,
  /** When true, `railExtras` moves out of the drawer and into the page flow on a
   * phone. Set by subpages whose own content doesn't own the stage. */
  inlineExtrasOnPhone: PropTypes.bool,
  /** Always-visible identity strip shown above the stage on compact screens. */
  summary: PropTypes.node,
  /** Operational main stage (relay + camera cards, or a nested subpage). */
  children: PropTypes.node,
  /** Desktop only: when true, the rail can be hidden entirely (caller renders the toggle). */
  collapsible: PropTypes.bool,
  /** Desktop only: current collapsed state (caller-owned — this component has no memory).
   * When true and collapsible, the rail is omitted entirely — no residual edge affordance. */
  collapsed: PropTypes.bool,
  /** 'page' (default) lets the stage grow with its content and the dashboard page-scrolls;
   * 'contained' bounds the stage to the viewport so its child (e.g. a data grid) scrolls internally. */
  stageScroll: PropTypes.oneOf(['page', 'contained']),
};

export default SiteDashboardShell;
